import React, { useState, useEffect, useCallback } from "react";
import { Button, Spinner, Table, Badge, Alert } from "react-bootstrap";
import { FaSyncAlt, FaPlug } from "react-icons/fa";
import axios from "axios";
import API_ROUTE from "../../utils/apiRoute";

const STATUS_BADGES = {
  ok: { variant: "success", label: "Ligado" },
  "needs-reconnect": { variant: "danger", label: "Precisa de religar" },
  "not-connected": { variant: "secondary", label: "Não ligado" },
  error: { variant: "warning", label: "Erro ao verificar" },
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("pt-PT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Lists the Zoom accounts of the spaces the user manages, checks them all
// against Zoom on demand, and lets the user reconnect only the broken ones.
const ZoomIntegrations = () => {
  const [integrations, setIntegrations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [lastCheckedAt, setLastCheckedAt] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const loadIntegrations = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_ROUTE}space/zoom/integrations`, {
        withCredentials: true,
      });
      setIntegrations(res.data?.data?.integrations || []);
    } catch (err) {
      setFeedback({ variant: "danger", text: "Não foi possível carregar as contas Zoom." });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIntegrations();
  }, [loadIntegrations]);

  useEffect(() => {
    const backendOrigin = new URL(API_ROUTE).origin;

    const handleZoomPopupMessage = (event) => {
      if (event.origin !== backendOrigin) return;

      if (event.data?.type === "ZOOM_CONNECTED") {
        setFeedback({ variant: "success", text: "Conta Zoom ligada com sucesso." });
        loadIntegrations();
      }

      if (event.data?.type === "ZOOM_CONNECT_ERROR") {
        setFeedback({
          variant: "danger",
          text: event.data?.message || "Não foi possível ligar a conta Zoom.",
        });
        loadIntegrations();
      }
    };

    window.addEventListener("message", handleZoomPopupMessage);
    return () => window.removeEventListener("message", handleZoomPopupMessage);
  }, [loadIntegrations]);

  const checkAll = async () => {
    setIsChecking(true);
    setFeedback(null);
    try {
      const res = await axios.post(`${API_ROUTE}space/zoom/integrations/check`, null, {
        withCredentials: true,
      });
      const checked = res.data?.data?.integrations || [];
      setIntegrations(checked);
      setLastCheckedAt(new Date());

      const broken = checked.filter((i) => i.status === "needs-reconnect").length;
      setFeedback(
        broken > 0
          ? { variant: "warning", text: `${broken} conta(s) Zoom precisam de ser religadas.` }
          : { variant: "success", text: "Todas as contas Zoom ligadas estão a funcionar." }
      );
    } catch (err) {
      setFeedback({ variant: "danger", text: "Não foi possível verificar as contas Zoom." });
    } finally {
      setIsChecking(false);
    }
  };

  const reconnect = async (spaceId) => {
    setFeedback(null);
    try {
      const res = await axios.get(`${API_ROUTE}space/${spaceId}/zoom/connect`, {
        withCredentials: true,
      });
      const authUrl = res.data?.data?.authUrl;
      if (!authUrl) throw new Error("Não foi possível obter o URL de autenticação do Zoom.");

      const popup = window.open(
        authUrl,
        "zoom-oauth-popup",
        "width=640,height=760,menubar=no,toolbar=no,location=yes,resizable=yes,scrollbars=yes,status=no"
      );
      if (!popup) {
        setFeedback({
          variant: "danger",
          text: "O popup foi bloqueado pelo navegador. Permita popups para continuar.",
        });
      }
    } catch (err) {
      setFeedback({
        variant: "danger",
        text: err?.response?.data?.message || err?.message || "Não foi possível iniciar a ligação da conta Zoom.",
      });
    }
  };

  return (
    <div style={{ background: "#f8f9fa", borderRadius: "8px", padding: "1rem", marginBottom: "1.5rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem", marginBottom: "0.75rem" }}>
        <div>
          <h5 style={{ margin: 0 }}>Contas Zoom</h5>
          <div style={{ fontSize: "0.8rem", color: "#6c757d" }}>
            {lastCheckedAt
              ? `Última verificação: ${formatDateTime(lastCheckedAt)}`
              : "Estado guardado. Use \"Verificar todas\" para confirmar com o Zoom."}
          </div>
        </div>
        <Button size="sm" onClick={checkAll} disabled={isChecking || isLoading}>
          {isChecking ? (
            <Spinner size="sm" animation="border" />
          ) : (
            <><FaSyncAlt style={{ marginRight: "6px" }} />Verificar todas</>
          )}
        </Button>
      </div>

      {feedback && (
        <Alert variant={feedback.variant} onClose={() => setFeedback(null)} dismissible style={{ fontSize: "0.85rem" }}>
          {feedback.text}
        </Alert>
      )}

      {isLoading ? (
        <div style={{ textAlign: "center", padding: "1rem" }}>
          <Spinner animation="border" variant="primary" />
        </div>
      ) : integrations.length === 0 ? (
        <div style={{ fontSize: "0.85rem", color: "#6c757d" }}>Nenhum espaço Zoom encontrado.</div>
      ) : (
        <Table size="sm" responsive hover style={{ fontSize: "0.85rem", marginBottom: 0, background: "#fff" }}>
          <thead>
            <tr>
              <th>Espaço</th>
              <th>Conta Zoom</th>
              <th>Estado</th>
              <th>Última renovação</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {integrations.map((integration) => {
              const badge = STATUS_BADGES[integration.status] || STATUS_BADGES.error;
              const canReconnect = integration.status !== "ok";
              return (
                <tr key={integration.spaceId}>
                  <td>{integration.spaceName}</td>
                  <td>{integration.zoomEmail || "—"}</td>
                  <td>
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                    {integration.message && (
                      <div style={{ fontSize: "0.72rem", color: "#6c757d" }}>{integration.message}</div>
                    )}
                  </td>
                  <td>{formatDateTime(integration.lastRefreshedAt)}</td>
                  <td style={{ textAlign: "right" }}>
                    {canReconnect && (
                      <Button size="sm" variant="outline-primary" onClick={() => reconnect(integration.spaceId)}>
                        <FaPlug style={{ marginRight: "4px" }} />
                        {integration.status === "not-connected" ? "Ligar" : "Religar"}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </div>
  );
};

export default ZoomIntegrations;
